import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import PageHeader from "src/app/components/PageHeader/PageHeader";
import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneFooter,
  PaneTopbar,
  PaneTopbarLeft,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import { useShowPane } from "src/hooks/useShowPane";
import { ReactComponent as CloseIcon } from "src/images/close.svg";
import { ReactComponent as TrashIcon } from "src/images/trash-can.svg";
import getAttachmentIcon from "src/app/Task/Attachments/utils/getAttachmentIcon";
import { CustomInputLabel } from "src/app/components/CustomInput/CustomInput";
import CustomTextarea from "src/app/components/CustomTextarea/CustomTextarea";
import { translateMessage } from "src/app/methods/translateMessage";
import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import { SkeletonRect } from "src/app/components/Skeleton/Skeleton";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { TooltipedEllipsis } from "src/app/components/TooltipedEllipsis/TooltipedEllipsis";
import { ReactComponent as DownloadIcon } from "src/images/download-rounded.svg";
import { openUrlInNewTab } from "src/utils/methods";
import useDebounce from "src/hooks/useDebounce";
import { FileDropzone } from "src/app/components/FileDropzone/FileDropzone";

import "./FileUploadPane.scss";

const renderSkeleton = () => {
  return Array.from({ length: 8 }).map((_, index) => (
    <SkeletonRect key={index} width={460} height={20} />
  ));
};

export interface FileUploadPaneConfig {
  title: {
    id: string;
    defaultMessage: string;
  };
  description: {
    id: string;
    defaultMessage: string;
  };
  textareaLabel: {
    id: string;
    defaultMessage: string;
  };
  textareaTooltip?: {
    id: string;
    defaultMessage: string;
  };
  textareaPlaceholder: {
    id: string;
    defaultMessage: string;
  };
  acceptedFileTypes: Record<string, string[]>;
  maxFileSizeMB?: number;
}

interface FileUploadPaneProps {
  config: FileUploadPaneConfig;
  onClose: () => void;
  handleUploadFile: (file: File) => Promise<string | undefined>;
  handleRemoveFile: (onSuccess: () => void) => void;
  handleSaveContent: (content: string) => void;
  content: string;
  fileName: string;
  fileUrl?: string;
  isLoading: boolean;
  className?: string;
  backdropClassName?: string;
  width?: number;
}

export default function FileUploadPane({
  config,
  onClose,
  handleUploadFile,
  handleRemoveFile,
  handleSaveContent,
  content,
  fileName,
  fileUrl,
  isLoading,
  className = "",
  backdropClassName = "",
  width = 500,
}: FileUploadPaneProps) {
  const [showPane, hidePane] = useShowPane(onClose);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>(fileName);
  const [textareaContent, setTextareaContent] = useState(content);
  const [isRemoveFileModalOpen, setIsRemoveFileModalOpen] = useState(false);

  const isUserEdit = useRef(false);
  const isTextareaFocused = useRef(false);

  const debouncedValue = useDebounce(textareaContent, 500);

  const intl = useIntl();
  const fileExtension = selectedFileName.split(".").pop();

  const handleResetFile = () => {
    setSelectedFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    isUserEdit.current = true;
    setTextareaContent(e.target.value);
  };

  const removeFunction = () => {
    handleRemoveFile(() => {
      handleResetFile();
    });
  };

  const handleDownloadFile = () => {
    if (!fileUrl) return;
    openUrlInNewTab(fileUrl);
  };

  const handleFileChange = async (files: File[]) => {
    const file = files[0];

    if (!file) return;

    const response = await handleUploadFile(file);
    if (response) {
      setTextareaContent(response);
      setSelectedFileName(file.name);
    }
  };

  useEffect(() => {
    if (isUserEdit.current) {
      handleSaveContent(debouncedValue);
      isUserEdit.current = false;
    }
  }, [debouncedValue, handleSaveContent]);

  useEffect(() => {
    if (!isTextareaFocused.current) {
      setTextareaContent(content);
    }
    setSelectedFileName(fileName);
  }, [content, fileName]);

  return (
    <>
      <Pane
        showPane={showPane}
        hidePane={hidePane}
        width={width}
        className={`file-upload-pane ${className}`}
        backdropClassName={`file-upload-pane__backdrop ${backdropClassName}`}
      >
        <PaneTopbar>
          <PaneTopbarLeft>
            <PageHeader
              title={
                <IDHFormattedMessage
                  id={config.title.id}
                  defaultMessage={config.title.defaultMessage}
                />
              }
              description={
                <IDHFormattedMessage
                  id={config.description.id}
                  defaultMessage={config.description.defaultMessage}
                />
              }
            />
          </PaneTopbarLeft>
        </PaneTopbar>
        <div className="pane__container">
          {!selectedFileName && (
            <FileDropzone
              accept={config.acceptedFileTypes}
              onChange={handleFileChange}
              maxSizeMB={config.maxFileSizeMB ?? 300}
              loading={isLoading}
            />
          )}
          {selectedFileName && (
            <div className="file-upload-pane__selected-file">
              <div className="file-upload-pane__selected-file-details">
                {getAttachmentIcon(fileExtension)}
                <Button
                  onClick={handleDownloadFile}
                  variant="transparent"
                  className="file-upload-pane__file-name"
                >
                  <TooltipedEllipsis
                    content={selectedFileName}
                    maxWidth={300}
                    className="text-h7-medium"
                  />
                </Button>
                {fileUrl && (
                  <Button
                    onClick={handleDownloadFile}
                    variant="white"
                    size="square"
                    className="file-upload-pane__download-btn"
                  >
                    <DownloadIcon />
                  </Button>
                )}
              </div>
              <Button
                disabled={isLoading}
                onClick={() => setIsRemoveFileModalOpen(true)}
                variant="white"
                size="square"
              >
                <TrashIcon />
              </Button>
            </div>
          )}
          <div className="file-upload-pane__content">
            <CustomInputLabel htmlFor="file-content-textarea">
              <IDHFormattedMessage
                id={config.textareaLabel.id}
                defaultMessage={config.textareaLabel.defaultMessage}
              />
              {config.textareaTooltip && (
                // @ts-expect-error
                <MaterialTooltip
                  id={config.textareaTooltip.id}
                  content={
                    <IDHFormattedMessage
                      id={config.textareaTooltip.id}
                      defaultMessage={config.textareaTooltip.defaultMessage}
                    />
                  }
                />
              )}
            </CustomInputLabel>
            {isLoading ? (
              renderSkeleton()
            ) : (
              <CustomTextarea
                id="file-content-textarea"
                ref={textareaRef}
                placeholder={translateMessage({
                  intl,
                  id: config.textareaPlaceholder.id,
                  defaultMessage: config.textareaPlaceholder.defaultMessage,
                })}
                value={textareaContent}
                onChange={handleChange}
                onFocus={() => {
                  isTextareaFocused.current = true;
                }}
                onBlur={() => {
                  isTextareaFocused.current = false;
                }}
                disabled={isLoading}
              />
            )}
          </div>
        </div>
        <PaneFooter>
          <div className="file-upload-pane__buttons">
            <Button
              size="medium"
              variant="white-with-light-grey-border"
              onClick={hidePane}
              dataQa="close-file-upload-pane-button"
            >
              <CloseIcon />
              <IDHFormattedMessage id="ws_close" defaultMessage="Close" />
            </Button>
          </div>
        </PaneFooter>
      </Pane>
      {isRemoveFileModalOpen && (
        <RemoveModal
          onClose={() => setIsRemoveFileModalOpen(false)}
          removeFunction={removeFunction}
          objectNames={[selectedFileName]}
        />
      )}
    </>
  );
}
